import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Language } from '@/types/multilingual'

// 项目列表返回类型
export interface MultilingualProjectItem {
  id: string
  category_id: string
  status: string
  image: string
  gallery: string[]
  link: string | null
  tags: string[]
  features: string[]
  tech_stack: string[]
  metrics: Record<string, string | number>
  start_date: string | null
  end_date: string | null
  sort_order: number
  is_featured: boolean
  created_at: string
  updated_at: string
  // 翻译字段
  title: string
  subtitle: string | null
  description: string
  content: string | null
}

// 获取多语言项目列表
export function useMultilingualProjects(categoryId?: string, language: Language = 'zh') {
  return useQuery({
    queryKey: ['multilingual-projects', categoryId, language],
    queryFn: async () => {
      // 首先获取项目
      let projectQuery = supabase
        .from('multilingual_projects')
        .select('*')
        .order('sort_order', { ascending: true })
      
      if (categoryId) {
        projectQuery = projectQuery.eq('category_id', categoryId)
      }
      
      const { data: projects, error: projectError } = await projectQuery
      
      if (projectError) throw projectError
      if (!projects || projects.length === 0) return []
      
      // 获取对应语言的翻译
      const projectIds = projects.map(p => p.id)
      const { data: translations, error: transError } = await supabase
        .from('project_translations')
        .select('*')
        .in('project_id', projectIds)
        .eq('language', language)
      
      if (transError) throw transError
      
      // 创建翻译映射
      const translationMap = new Map(
        translations?.map(t => [t.project_id, t]) || []
      )
      
      // 合并数据
      return projects.map(project => {
        const trans = translationMap.get(project.id)
        return {
          ...project,
          gallery: Array.isArray(project.gallery) ? project.gallery : [],
          tags: Array.isArray(project.tags) ? project.tags : [],
          features: Array.isArray(project.features) ? project.features : [],
          tech_stack: Array.isArray(project.tech_stack) ? project.tech_stack : [],
          metrics: typeof project.metrics === 'object' && project.metrics ? project.metrics : {},
          title: trans?.title || project.id,
          subtitle: trans?.subtitle || null,
          description: trans?.description || '',
          content: trans?.content || null
        } as MultilingualProjectItem
      })
    }
  })
}

// 获取单个多语言项目
export function useMultilingualProject(projectId: string, language: Language = 'zh') {
  return useQuery({
    queryKey: ['multilingual-project', projectId, language],
    queryFn: async () => {
      // 获取项目
      const { data: project, error: projectError } = await supabase
        .from('multilingual_projects')
        .select('*')
        .eq('id', projectId)
        .maybeSingle()
      
      if (projectError) throw projectError
      if (!project) return null
      
      // 获取翻译
      const { data: trans, error: transError } = await supabase
        .from('project_translations')
        .select('*')
        .eq('project_id', projectId)
        .eq('language', language)
        .maybeSingle()
      
      if (transError) throw transError
      
      return {
        ...project,
        gallery: Array.isArray(project.gallery) ? project.gallery : [],
        tags: Array.isArray(project.tags) ? project.tags : [],
        features: Array.isArray(project.features) ? project.features : [],
        tech_stack: Array.isArray(project.tech_stack) ? project.tech_stack : [],
        metrics: typeof project.metrics === 'object' && project.metrics ? project.metrics : {},
        title: trans?.title || project.id,
        subtitle: trans?.subtitle || null,
        description: trans?.description || '',
        content: trans?.content || null
      } as MultilingualProjectItem
    },
    enabled: !!projectId
  })
}

// 删除项目
export function useDeleteMultilingualProject() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (id: string) => {
      // 先删除翻译
      const { error: transError } = await supabase
        .from('project_translations')
        .delete()
        .eq('project_id', id)
      
      if (transError) throw transError
      
      // 再删除项目
      const { error } = await supabase
        .from('multilingual_projects')
        .delete()
        .eq('id', id)
      
      if (error) throw error
      
      return { id }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['multilingual-projects'] })
    }
  })
}

// 创建项目
export function useCreateMultilingualProject() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: {
      project: {
        id: string
        category_id: string
        status: string
        image: string
        gallery?: string[]
        link?: string
        tags?: string[]
        features?: string[]
        tech_stack?: string[]
        metrics?: Record<string, string | number>
        sort_order?: number
        is_featured?: boolean
      }
      translations: {
        zh: { title: string; subtitle?: string; description: string; content?: string }
        en: { title: string; subtitle?: string; description: string; content?: string }
      }
    }) => {
      // 插入项目
      const { error: projectError } = await supabase
        .from('multilingual_projects')
        .insert({
          id: data.project.id,
          category_id: data.project.category_id,
          status: data.project.status,
          image: data.project.image,
          gallery: data.project.gallery || [],
          link: data.project.link || null,
          tags: data.project.tags || [],
          features: data.project.features || [],
          tech_stack: data.project.tech_stack || [],
          metrics: data.project.metrics || {},
          sort_order: data.project.sort_order || 0,
          is_featured: data.project.is_featured || false
        })
      
      if (projectError) throw projectError
      
      // 插入翻译
      const translations = [
        { project_id: data.project.id, language: 'zh', ...data.translations.zh },
        { project_id: data.project.id, language: 'en', ...data.translations.en }
      ]
      
      const { error: transError } = await supabase
        .from('project_translations')
        .insert(translations)
      
      if (transError) throw transError
      
      return { id: data.project.id }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['multilingual-projects'] })
    }
  })
}

// 更新项目
export function useUpdateMultilingualProject() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: {
      id: string
      project?: Partial<{
        category_id: string
        status: string
        image: string
        gallery: string[]
        link: string | null
        tags: string[]
        features: string[]
        tech_stack: string[]
        metrics: Record<string, string | number>
        sort_order: number
        is_featured: boolean
      }>
      translations?: {
        zh?: { title?: string; subtitle?: string; description?: string; content?: string }
        en?: { title?: string; subtitle?: string; description?: string; content?: string }
      }
    }) => {
      // 更新项目基本信息
      if (data.project) {
        const { error: projectError } = await supabase
          .from('multilingual_projects')
          .update(data.project)
          .eq('id', data.id)
        
        if (projectError) throw projectError
      }
      
      // 更新翻译
      if (data.translations) {
        for (const [lang, trans] of Object.entries(data.translations)) {
          if (trans) {
            const { error: transError } = await supabase
              .from('project_translations')
              .update(trans)
              .eq('project_id', data.id)
              .eq('language', lang)
            
            if (transError) throw transError
          }
        }
      }
      
      return { id: data.id }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['multilingual-projects'] })
    }
  })
}
